import { ClickFeature } from '@audacy-clients/client-services/core';
import { useLiveEpisodeFromSchedule } from '@audacy-clients/core/api/queries/useLiveEpisodeFromSchedule';
import type { IModule } from '@audacy-clients/core/atoms/wrappers/modules';
import { useModuleContext } from '@audacy-clients/core/components/ModuleRenderer/context';
import type { IPlayContext } from '@audacy-clients/core/hooks/useAudioHelpers';
import { getPlayingUntilFromStartAndDuration } from '@audacy-clients/core/utils/date';

import type { INowPlaying } from '~/components/Entity/types';
import { useAudioHelpersWeb } from '~/hooks/use-audio-helpers-web';
import { getStationScheduleUrl } from '~/routes';
import { useNavClickEvent } from '~/utils/analytics';

import LiveNow from './LiveNow';

interface ILiveNowWithState {
  isStation: boolean;
  module: IModule;
}

const LiveNowWithState = ({ isStation, module }: ILiveNowWithState): JSX.Element | null => {
  const {
    viewRootObjectId: contentId,
    viewRootObjectUrl: stationUrl,
    viewRootObjectRewindable: isRewindable,
  } = useModuleContext();
  const audioHelpers = useAudioHelpersWeb();
  const { liveEpisode } = useLiveEpisodeFromSchedule({ contentId, isStation });

  const trackNavigation = useNavClickEvent({
    contentId: liveEpisode?.id,
    feature: ClickFeature.CTA_BUTTON,
    moduleId: module.id,
  });

  const playContext: IPlayContext = {
    feature: ClickFeature.MODULE,
    moduleId: module.id,
  };

  if (!contentId || !liveEpisode) return null;

  const liveUntil: INowPlaying | undefined =
    liveEpisode?.startDateTime && liveEpisode?.duration
      ? {
          playingUntil: getPlayingUntilFromStartAndDuration(
            liveEpisode.startDateTime,
            liveEpisode.duration,
          ),
        }
      : undefined;

  const onPlayLiveItem = () => {
    // We can assume LiveNow broadcast episode's are live. Only ScheduleItem
    // broadcasts are rewindable for now.
    audioHelpers.playEpisodeLive(liveEpisode, playContext);
  };

  const scheduleHref = stationUrl ? getStationScheduleUrl(stationUrl) : '';

  return (
    <>
      <LiveNow
        episode={liveEpisode}
        isStation={isStation}
        onPlayLiveItem={onPlayLiveItem}
        onFooterClick={trackNavigation}
        liveUntil={liveUntil}
        scheduleHref={scheduleHref}
        contentId={liveEpisode.id}
        playContext={playContext}
        isRewindable={isRewindable}
      />
    </>
  );
};

export default LiveNowWithState;
